import { cloneDeep } from "lodash";

import { BusinessGoal, Plan, Project, Scenario, UUID } from "@/types/project";

export type ProjectExploreReportFilters = {
  advertiser?: string[];
  business_goal?: string[];
  b2b_b2c?: string[];
  media_type?: string[];
  start_time?: string;
  end_time?: string;
};

/**
 * ProjectService centralizes project-scoped utilities. It is instantiated with a
 * single project object and then reused as a lightweight read-only façade. This
 * keeps traversal logic (business goals → plans → scenarios) in one place and
 * prevents it from being duplicated across views.
 *
 * Responsibilities:
 * - expose convenience accessors for fetching scenarios, plans, and business
 *   goals by their identifiers without re-implementing nested loops
 * - provide domain-specific helpers such as "pickPlanScenarioFilter" that0
 *   encapsulate fallback logic for selecting the most relevant scenario
 * - share utilities for preparing scenario payloads before persisting them via
 *   the API (see prepareScenarioForSave)
 */

export class ProjectService {
  private project: Project;

  /**
   * Cache the project snapshot. The instance can then be reused as a
   * read-through object without re-fetching or cloning the project tree.
   */
  constructor(project: Project) {
    this.project = project;
  }

  /**
   * Return a flat list of all scenarios known in the project across all
   * business goals and plans.
   */
  getAllScenarios(): Scenario[] {
    const scenarios: Scenario[] = [];

    for (const businessGoal of this.project.business_goals ?? []) {
      for (const plan of businessGoal.plans ?? []) {
        scenarios.push(...(plan.scenarios ?? []));
      }
    }

    return scenarios;
  }

  /**
   * Traverse every business goal and plan to locate the first scenario that
   * matches the provided identifier. Returns undefined when the scenario is not
   * present, allowing callers to handle missing data gracefully.
   */
  getScenarioById(scenarioId: string): Scenario | undefined {
    for (const bg of this.project.business_goals ?? []) {
      for (const plan of bg.plans ?? []) {
        const scenario = (plan.scenarios ?? []).find((s) => s.id === scenarioId);
        if (scenario) {
          return scenario;
        }
      }
    }
    return undefined;
  }

  /**
   * Provides direct access to a plan by its id without requiring the caller to
   * loop through business goals. Useful for UI handlers that hold only plan
   * metadata after user interaction.
   */
  getPlanById(planId: string): Plan | undefined {
    for (const bg of this.project.business_goals ?? []) {
      const plan = (bg.plans ?? []).find((p) => p.id === planId);
      if (plan) {
        return plan;
      }
    }
    return undefined;
  }

  /**
   * Fetch a business goal by its id from the cached project. Returns undefined
   * when the id is not found, which allows the caller to defer to alternate
   * fallbacks.
   */
  getBusinessGoalById(businessGoalId: UUID): BusinessGoal | undefined {
    return (this.project.business_goals ?? []).find((bg) => bg.id === businessGoalId);
  }

  /**
   * Identifies the business goal that currently owns a given plan. Needed when a
   * plan is moved between goals and the previous owner has to be located before
   * the plan can be detached from it.
   */
  getBusinessGoalByPlanId(planId: string): BusinessGoal | undefined {
    return (this.project.business_goals ?? []).find((bg) => (bg.plans ?? []).some((p) => p.id === planId));
  }

  /**
   * Identifies the business goal that owns a given scenario. Useful when only a
   * scenario id is known yet goal-level metadata (e.g., b2b_or_b2c) is needed.
   */
  getBusinessGoalByScenarioId(scenarioId: string): BusinessGoal | undefined {
    for (const bg of this.project.business_goals ?? []) {
      for (const plan of bg.plans ?? []) {
        if ((plan.scenarios ?? []).some((s) => s.id === scenarioId)) {
          return bg;
        }
      }
    }
    return undefined;
  }

  /**
   * Identifies the plan that owns a given scenario. This is helpful when
   * external code only knows the scenario id (e.g., from table filters) yet
   * needs to mutate plan-level data.
   */
  getPlanByScenarioId(scenarioId: string): Plan | undefined {
    for (const bg of this.project.business_goals ?? []) {
      for (const plan of bg.plans ?? []) {
        const scenario = (plan.scenarios ?? []).find((s) => s.id === scenarioId);
        if (scenario) {
          return plan;
        }
      }
    }
    return undefined;
  }

  /**
   * Determine which scenario id should be used as the active filter for a plan
   * by preferring approved scenarios and falling back to the first available
   * scenario. Returns undefined if the plan has no scenarios.
   */
  pickPlanScenarioFilter(planId: string): UUID | undefined {
    const plan = this.getPlanById(planId);
    if (!plan) return undefined;

    const scenarios = plan.scenarios ?? [];
    if (scenarios.length === 0) return undefined;

    const approvedScenario = scenarios.find((s) => s.approved === true);
    if (approvedScenario) {
      return approvedScenario.id;
    }

    return scenarios[0].id;
  }

  /**
   * Produce a save-ready clone of the project where every channel selection that
   * has a single flight with a zero budget is nullified (amount and percentage
   * set to null). Output is left untouched - only the input channel_selections
   * are normalized. Returns a deep clone so the cached project stays intact.
   */
  prepareForSave(): Project {
    const next = cloneDeep(this.project);

    for (const bg of next.business_goals ?? []) {
      for (const plan of bg.plans ?? []) {
        for (const scenario of plan.scenarios ?? []) {
          for (const kpi of scenario.campaign_kpis ?? []) {
            for (const selection of kpi.channel_selections ?? []) {
              const flights = selection.flights ?? [];
              if (flights.length === 1 && Number(flights[0]?.amount ?? 0) === 0) {
                flights[0].amount = null;
                flights[0].percentage = null;
              }
            }
          }
        }
      }
    }

    return next;
  }

  getExploreReportFilters(): ProjectExploreReportFilters {
    const businessGoals = new Set<string>();
    const b2bB2cValues = new Set<string>();
    const mediaTypes = new Set<string>();

    let earliestStart: string | null = null;
    let latestEnd: string | null = null;

    for (const goal of this.project.business_goals ?? []) {
      const businessGoal = String(goal.business_goal ?? "").trim();
      if (businessGoal) {
        businessGoals.add(businessGoal);
      }

      const b2bB2c = String(goal.b2b_or_b2c ?? "")
        .trim()
        .toLowerCase();
      if (b2bB2c) {
        b2bB2cValues.add(b2bB2c);
      }

      for (const plan of goal.plans ?? []) {
        for (const scenario of plan.scenarios ?? []) {
          const startDate = String(scenario.start_date ?? "").trim();
          const endDate = String(scenario.end_date ?? "").trim();

          if (startDate && (!earliestStart || startDate < earliestStart)) {
            earliestStart = startDate;
          }

          if (endDate && (!latestEnd || endDate > latestEnd)) {
            latestEnd = endDate;
          }

          for (const row of scenario.output?.data ?? []) {
            const mediaType = String(row.media_type ?? "").trim();
            if (mediaType) {
              mediaTypes.add(mediaType);
            }
          }
        }
      }
    }

    const advertiser = String(this.project.advertiser_profile?.name ?? "").trim();

    return {
      ...(advertiser ? { advertiser: [advertiser] } : {}),
      ...(businessGoals.size ? { business_goal: Array.from(businessGoals) } : {}),
      ...(b2bB2cValues.size ? { b2b_b2c: Array.from(b2bB2cValues) } : {}),
      ...(mediaTypes.size ? { media_type: Array.from(mediaTypes) } : {}),
      ...(earliestStart ? { start_time: earliestStart } : {}),
      ...(latestEnd ? { end_time: latestEnd } : {}),
    };
  }
}
